import { useMemo } from "react";
import { buildBodyRootMap } from "../../utils/bodyHierarchy";

export function useSceneRootTargets({ bodies, effectiveWorldBodies, safeBodies, worldBodies }) {
  const bodyRootMap = useMemo(
    () => buildBodyRootMap(bodies || {}, worldBodies || []),
    [bodies, worldBodies]
  );

  const primaryRobotRootUuid = useMemo(() => {
    const roots = (worldBodies || [])
      .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
      .filter(Boolean);
    if (roots.length === 0) return null;
    if (roots.length === 1) return roots[0];

    const countJointsInSubtree = (rootUuid) => {
      let count = 0;
      const visited = new Set();
      const stack = [rootUuid];
      while (stack.length > 0) {
        const bodyUuid = stack.pop();
        if (!bodyUuid || visited.has(bodyUuid)) continue;
        visited.add(bodyUuid);
        const body = bodies?.[bodyUuid];
        if (!body) continue;
        count += Array.isArray(body.joints) ? body.joints.length : 0;
        (body.children || []).forEach((childUuid) => {
          if (childUuid && !visited.has(childUuid)) stack.push(childUuid);
        });
      }
      return count;
    };

    let bestRoot = roots[0];
    let bestScore = countJointsInSubtree(bestRoot);
    for (let i = 1; i < roots.length; i += 1) {
      const candidate = roots[i];
      const score = countJointsInSubtree(candidate);
      if (score > bestScore) {
        bestRoot = candidate;
        bestScore = score;
      }
    }
    return bestRoot;
  }, [worldBodies, bodies]);

  const worldRootTargetBodyUuid = useMemo(() => {
    if (primaryRobotRootUuid && safeBodies?.[primaryRobotRootUuid]) return primaryRobotRootUuid;
    const firstRootUuid = (effectiveWorldBodies || [])
      .map((entry) => (typeof entry === "string" ? entry : entry?.uuid || entry?.rootBodyUuid))
      .find((uuid) => !!uuid && !!safeBodies?.[uuid]);
    return firstRootUuid || null;
  }, [primaryRobotRootUuid, effectiveWorldBodies, safeBodies]);

  return { bodyRootMap, worldRootTargetBodyUuid };
}
